import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import type { Tenant } from '@core/api/tenant';
import type { CreateUserData, User } from '@core/api/user';
import { createUser, updateUser } from '@core/api/user';
import ProfileForm, {
  type ProfileFormValues,
  getProfileDefaultValues,
  toProfilePayload,
} from '@core/components/forms/ProfileForm';
import { RhfSelect } from '@core/components/forms/fields';
import { useAuth } from '@core/contexts/AuthContext';
import { UserRole, getAssignableUserRoleOptions } from '@core/enum/userRole';
import FormDialog from './FormDialog';

export interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  user?: User | null;
  tenant?: Tenant | null;
  onSuccess?: () => void;
  onUserCreated?: (user: User) => void;
}

type UserFormValues = ProfileFormValues & {
  role: UserRole | '';
};

const UserDialog: React.FC<UserDialogProps> = ({
  open,
  onClose,
  user,
  tenant,
  onSuccess,
  onUserCreated,
}) => {
  const { user: currentUser } = useAuth();

  const roleOptions = useMemo(
    () => (currentUser ? getAssignableUserRoleOptions(currentUser.role) : []),
    [currentUser]
  );

  const defaultValues = useMemo<UserFormValues>(
    () => ({
      ...getProfileDefaultValues(user),
      role: user?.role ?? '',
    }),
    [user]
  );

  const onSubmit = async (data: UserFormValues) => {
    const payload: CreateUserData = {
      ...toProfilePayload(data),
      role: data.role as UserRole,
      ...(tenant != null ? { tenant: tenant.id } : {}),
    };

    if (user) {
      await updateUser(user.id, payload);
      toast.success('User updated successfully');
    } else {
      const created = await createUser(payload);
      toast.success('User created successfully');
      onUserCreated?.(created);
    }
    onSuccess?.();
  };

  return (
    <FormDialog<UserFormValues>
      open={open}
      onClose={onClose}
      title={user ? 'Edit User' : 'Add User'}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={user ? 'Update' : 'Add'}
      cancelText="Cancel"
    >
      <ProfileForm />
      <RhfSelect<UserFormValues>
        name="role"
        label="Role"
        options={roleOptions}
        required
      />
    </FormDialog>
  );
};

export default UserDialog;
